import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  ArrowLeft,
  Lamp,
  Plus,
  Trash2,
  Loader2,
  Pencil,
  Link2,
  Crosshair,
  MoreHorizontal,
  SquareArrowOutUpRight,
  GripVertical,
} from 'lucide-react';
import { API_BASE } from '../config/api';
import LanguageSwitcher from '../components/LanguageSwitcher';
import CricketRichCardModal from '../components/cricket/CricketRichCardModal';
import CricketCardActionsMenu from '../components/cricket/CricketCardActionsMenu';
import CricketBrandedBackdrop from '../components/cricket/CricketBrandedBackdrop';
import { htmlToPlainPreview, parseCricketChecklist, cardCoverHex } from '../utils/cricketCardUtils';
import {
  isCricketFreeStandaloneLayout,
  loadCricketFreeStandalone,
  saveCricketFreeStandalone,
  newStandaloneCricketId,
} from '../utils/cricketFreeStandalone';

function authHeaders(token) {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

const CRICKET_LIST_LAMP_HEX = { todo: '#f59e0b', doing: '#3b82f6', done: '#22c55e' };

const CRICKET_MOVE_PANEL_EXTRA_DOWN_PX = 50;

function buildColumnState(lists, cards) {
  const sortedLists = [...(lists || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const cardsById = Object.fromEntries((cards || []).map((c) => [c.id, { ...c }]));
  const columnCardIds = {};
  for (const l of sortedLists) {
    columnCardIds[l.id] = (cards || [])
      .filter((c) => c.list_id === l.id)
      .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) || String(a.created_at || '').localeCompare(String(b.created_at || '')))
      .map((c) => c.id);
  }
  return { sortedLists, columnCardIds, cardsById };
}

/**
 * Página completa do quadro Cricket (Kanban) para um layout salvo na nuvem.
 */
export default function CricketBoardPage({
  layoutId,
  authToken,
  /** Obrigatório quando layoutId é o quadro Free standalone (sem palco na nuvem). */
  freeStandaloneUserId,
  darkMode,
  onToggleDarkMode,
  onBack,
  showToast,
  onFocusLinkedWidget,
}) {
  const { t, i18n } = useTranslation(['cricket', 'common']);
  const isStandalone = isCricketFreeStandaloneLayout(layoutId);
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState(null);
  const [projectName, setProjectName] = useState('');
  const [readOnly, setReadOnly] = useState(false);
  const [sortedLists, setSortedLists] = useState([]);
  const [columnCardIds, setColumnCardIds] = useState({});
  const [cardsById, setCardsById] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [newTitleByList, setNewTitleByList] = useState({});
  const [editingCardId, setEditingCardId] = useState(null);
  const [editTitleDraft, setEditTitleDraft] = useState('');
  /** Lista com compositor «Adicionar um cartão» aberto (estilo Trello). */
  const [composerListId, setComposerListId] = useState(null);
  const composerInputRef = useRef(null);
  const [moveMenuCardId, setMoveMenuCardId] = useState(null);
  const [moveTargetListId, setMoveTargetListId] = useState('');
  const [movePanelPos, setMovePanelPos] = useState(null);
  const [richModalCardId, setRichModalCardId] = useState(null);
  const [moreMenu, setMoreMenu] = useState(null);
  const [editingListId, setEditingListId] = useState(null);
  const [editListTitleDraft, setEditListTitleDraft] = useState('');
  const [newListOpen, setNewListOpen] = useState(false);
  const [newListTitle, setNewListTitle] = useState('');

  useEffect(() => {
    if (composerListId && composerInputRef.current) {
      composerInputRef.current.focus();
    }
  }, [composerListId]);

  useEffect(() => {
    if (!moveMenuCardId) return undefined;
    const onDoc = (e) => {
      const el = e.target;
      if (!(el instanceof Element)) return;
      if (el.closest('[data-cricket-actions-root]')) return;
      if (el.closest('[data-cricket-board-move-portal]')) return;
      if (el.closest(`[data-cricket-board-card="${moveMenuCardId}"]`)) return;
      setMoveMenuCardId(null);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [moveMenuCardId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (richModalCardId) {
        setRichModalCardId(null);
        return;
      }
      if (moreMenu) {
        setMoreMenu(null);
        return;
      }
      if (editingCardId) {
        setEditingCardId(null);
        return;
      }
      if (moveMenuCardId) {
        setMoveMenuCardId(null);
        return;
      }
      if (editingListId) {
        setEditingListId(null);
        return;
      }
      if (composerListId) {
        setComposerListId(null);
        setNewTitleByList((m) => ({ ...m, [composerListId]: '' }));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [richModalCardId, moreMenu, editingCardId, moveMenuCardId, composerListId, editingListId]);

  const notify = useCallback(
    (msg, type) => {
      if (typeof showToast === 'function') showToast(msg, type || 'warning');
    },
    [showToast],
  );

  const loadAll = useCallback(async () => {
    if (!layoutId) return;
    if (isStandalone) {
      if (!freeStandaloneUserId) {
        setErrorKey('loadError');
        setLoading(false);
        return;
      }
      setLoading(true);
      setReadOnly(false);
      setErrorKey(null);
      try {
        const { lists, cards } = loadCricketFreeStandalone(freeStandaloneUserId);
        const { sortedLists: sl, columnCardIds: col, cardsById: byId } = buildColumnState(lists, cards);
        setSortedLists(sl);
        setColumnCardIds(col);
        setCardsById(byId);
        setProjectName(t('cricket:freeStandaloneSubtitle'));
      } catch {
        setErrorKey('loadError');
      } finally {
        setLoading(false);
      }
      return;
    }
    if (!authToken) return;
    setLoading(true);
    setReadOnly(false);
    setErrorKey(null);
    try {
      const [projRes, cricketRes] = await Promise.all([
        fetch(`${API_BASE}/layouts/${encodeURIComponent(layoutId)}`, { headers: { Authorization: `Bearer ${authToken}` } }),
        fetch(`${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket`, { headers: { Authorization: `Bearer ${authToken}` } }),
      ]);
      if (projRes.status === 403) {
        setErrorKey('forbidden');
        setLoading(false);
        return;
      }
      if (!projRes.ok) {
        setErrorKey('loadError');
        setLoading(false);
        return;
      }
      const projData = await projRes.json();
      const project = projData.project;
      if (project?.type === 'folder') {
        setErrorKey('loadError');
        setLoading(false);
        return;
      }
      setProjectName(project?.course_name || '');
      const cp = projData.collabPermissions;
      const ro = cp != null && cp.canEdit === false;
      setReadOnly(ro);

      if (cricketRes.status === 403) {
        const errBody = await cricketRes.json().catch(() => ({}));
        const msg = (errBody.error || '').toLowerCase();
        setErrorKey(msg.includes('plano') || msg.includes('plan') ? 'planLocked' : 'forbidden');
        setLoading(false);
        return;
      }
      if (!cricketRes.ok) {
        setErrorKey('loadError');
        setLoading(false);
        return;
      }
      const data = await cricketRes.json();
      const { sortedLists: sl, columnCardIds: col, cardsById: byId } = buildColumnState(data.lists, data.cards);
      setSortedLists(sl);
      setColumnCardIds(col);
      setCardsById(byId);
    } catch {
      setErrorKey('loadError');
    } finally {
      setLoading(false);
    }
  }, [layoutId, authToken, isStandalone, freeStandaloneUserId, t]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const persistStandaloneCardPatch = useCallback(
    async (cardId, payload) => {
      if (!freeStandaloneUserId) return false;
      try {
        const raw = loadCricketFreeStandalone(freeStandaloneUserId);
        const ckJson =
          payload.checklist !== undefined
            ? payload.checklist == null
              ? null
              : JSON.stringify(payload.checklist)
            : undefined;
        const now = new Date().toISOString();
        const cards = raw.cards.map((c) => {
          if (c.id !== cardId) return c;
          const next = { ...c, updated_at: now };
          if (payload.title !== undefined) next.title = String(payload.title || '').trim();
          if (payload.body !== undefined) next.body = payload.body;
          if (payload.list_id !== undefined) next.list_id = payload.list_id;
          if (payload.due_at !== undefined) next.due_at = payload.due_at;
          if (Object.prototype.hasOwnProperty.call(payload, 'cover_color')) {
            next.cover_color = payload.cover_color;
          }
          if (ckJson !== undefined) next.checklist_json = ckJson;
          return next;
        });
        saveCricketFreeStandalone(freeStandaloneUserId, { lists: raw.lists, cards });
        await loadAll();
        return true;
      } catch {
        return false;
      }
    },
    [freeStandaloneUserId, loadAll],
  );

  const duplicateStandaloneCard = useCallback(
    async (cardId, listIdParam) => {
      if (!freeStandaloneUserId) return false;
      try {
        const raw = loadCricketFreeStandalone(freeStandaloneUserId);
        const src = raw.cards.find((c) => c.id === cardId);
        if (!src) return false;
        const destListId = listIdParam || src.list_id;
        const newId = newStandaloneCricketId();
        const sameList = raw.cards.filter((c) => c.list_id === destListId);
        const sortOrder =
          sameList.length > 0 ? Math.max(...sameList.map((c) => Number(c.sort_order) || 0)) + 1 : 0;
        const now = new Date().toISOString();
        const copy = {
          ...src,
          id: newId,
          list_id: destListId,
          widget_id: null,
          sort_order: sortOrder,
          created_at: now,
          updated_at: now,
        };
        saveCricketFreeStandalone(freeStandaloneUserId, { lists: raw.lists, cards: [...raw.cards, copy] });
        await loadAll();
        return true;
      } catch {
        return false;
      }
    },
    [freeStandaloneUserId, loadAll],
  );

  const persistColumnOrder = useCallback(
    async (nextColumnCardIds) => {
      if (readOnly) return;
      if (isStandalone && freeStandaloneUserId) {
        setSyncing(true);
        try {
          const raw = loadCricketFreeStandalone(freeStandaloneUserId);
          const byId = Object.fromEntries(raw.cards.map((c) => [c.id, { ...c }]));
          for (const listId of Object.keys(nextColumnCardIds)) {
            const ids = nextColumnCardIds[listId];
            for (let i = 0; i < ids.length; i++) {
              const id = ids[i];
              if (byId[id]) {
                byId[id].list_id = listId;
                byId[id].sort_order = i;
              }
            }
          }
          saveCricketFreeStandalone(freeStandaloneUserId, { lists: raw.lists, cards: Object.values(byId) });
          await loadAll();
        } catch {
          notify(t('cricket:syncError'), 'error');
          await loadAll();
        } finally {
          setSyncing(false);
        }
        return;
      }
      if (!authToken) return;
      setSyncing(true);
      try {
        for (const listId of Object.keys(nextColumnCardIds)) {
          const ids = nextColumnCardIds[listId];
          for (let i = 0; i < ids.length; i++) {
            const id = ids[i];
            const res = await fetch(`${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket/cards/${encodeURIComponent(id)}`, {
              method: 'PATCH',
              headers: authHeaders(authToken),
              body: JSON.stringify({ list_id: listId, sort_order: i }),
            });
            if (!res.ok) throw new Error('patch');
          }
        }
        await loadAll();
      } catch {
        notify(t('cricket:syncError'), 'error');
        await loadAll();
      } finally {
        setSyncing(false);
      }
    },
    [authToken, layoutId, loadAll, notify, readOnly, t, isStandalone, freeStandaloneUserId],
  );

  const persistListOrder = useCallback(
    async (orderedListIds) => {
      if (readOnly) return;
      if (isStandalone && freeStandaloneUserId) {
        setSyncing(true);
        try {
          const raw = loadCricketFreeStandalone(freeStandaloneUserId);
          const byId = Object.fromEntries(raw.lists.map((l) => [l.id, { ...l }]));
          const reordered = orderedListIds.map((id) => byId[id]).filter(Boolean);
          reordered.forEach((l, i) => {
            l.sort_order = i;
          });
          saveCricketFreeStandalone(freeStandaloneUserId, { lists: reordered, cards: raw.cards });
          await loadAll();
        } catch {
          notify(t('cricket:listSyncError'), 'error');
          await loadAll();
        } finally {
          setSyncing(false);
        }
        return;
      }
      if (!authToken) return;
      setSyncing(true);
      try {
        const res = await fetch(`${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket/lists/reorder`, {
          method: 'PATCH',
          headers: authHeaders(authToken),
          body: JSON.stringify({ order: orderedListIds }),
        });
        if (!res.ok) throw new Error('reorder');
        await loadAll();
      } catch {
        notify(t('cricket:listSyncError'), 'error');
        await loadAll();
      } finally {
        setSyncing(false);
      }
    },
    [authToken, layoutId, loadAll, notify, readOnly, t, isStandalone, freeStandaloneUserId],
  );

  const commitListTitle = async (listId) => {
    if (readOnly) return;
    const prev = sortedLists.find((l) => l.id === listId);
    const title = editListTitleDraft.trim();
    if (!title) {
      setEditingListId(null);
      return;
    }
    if (prev && prev.title === title) {
      setEditingListId(null);
      return;
    }
    if (isStandalone && freeStandaloneUserId) {
      try {
        const raw = loadCricketFreeStandalone(freeStandaloneUserId);
        const lists = raw.lists.map((l) => (l.id === listId ? { ...l, title } : l));
        saveCricketFreeStandalone(freeStandaloneUserId, { lists, cards: raw.cards });
        setEditingListId(null);
        await loadAll();
      } catch {
        notify(t('cricket:listSyncError'), 'error');
      }
      return;
    }
    if (!authToken) return;
    try {
      const res = await fetch(`${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket/lists/${encodeURIComponent(listId)}`, {
        method: 'PATCH',
        headers: authHeaders(authToken),
        body: JSON.stringify({ title }),
      });
      if (!res.ok) {
        notify(t('cricket:listSyncError'), 'error');
        return;
      }
      setEditingListId(null);
      await loadAll();
    } catch {
      notify(t('cricket:listSyncError'), 'error');
    }
  };

  const handleCreateList = async () => {
    if (readOnly) return;
    const title = newListTitle.trim();
    if (!title) return;
    if (isStandalone && freeStandaloneUserId) {
      try {
        const raw = loadCricketFreeStandalone(freeStandaloneUserId);
        const newList = {
          id: newStandaloneCricketId(),
          board_id: null,
          list_key: 'custom',
          title,
          sort_order: raw.lists.length,
        };
        saveCricketFreeStandalone(freeStandaloneUserId, { lists: [...raw.lists, newList], cards: raw.cards });
        setNewListTitle('');
        setNewListOpen(false);
        await loadAll();
      } catch {
        notify(t('cricket:createListError'), 'error');
      }
      return;
    }
    if (!authToken) return;
    try {
      const res = await fetch(`${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket/lists`, {
        method: 'POST',
        headers: authHeaders(authToken),
        body: JSON.stringify({ title }),
      });
      if (!res.ok) {
        notify(t('cricket:createListError'), 'error');
        return;
      }
      setNewListTitle('');
      setNewListOpen(false);
      await loadAll();
    } catch {
      notify(t('cricket:createListError'), 'error');
    }
  };

  const onDragEnd = useCallback(
    (result) => {
      if (readOnly) return;
      const { destination, source, type } = result;
      if (!destination) return;
      if (type === 'COLUMN') {
        if (destination.droppableId !== 'cricket-columns' || source.droppableId !== 'cricket-columns') return;
        if (destination.index === source.index) return;
        const next = Array.from(sortedLists);
        const [removed] = next.splice(source.index, 1);
        next.splice(destination.index, 0, removed);
        setSortedLists(next);
        persistListOrder(next.map((l) => l.id));
        return;
      }
      if (destination.droppableId === source.droppableId && destination.index === source.index) return;

      const sourceListId = source.droppableId;
      const destListId = destination.droppableId;
      const start = Array.from(columnCardIds[sourceListId] || []);
      const [removed] = start.splice(source.index, 1);
      if (!removed) return;

      let nextCols = { ...columnCardIds };
      if (sourceListId === destListId) {
        start.splice(destination.index, 0, removed);
        nextCols[sourceListId] = start;
      } else {
        const end = Array.from(columnCardIds[destListId] || []);
        end.splice(destination.index, 0, removed);
        nextCols[sourceListId] = start;
        nextCols[destListId] = end;
      }

      setColumnCardIds(nextCols);
      setCardsById((prev) => {
        const next = { ...prev };
        if (next[removed]) {
          next[removed] = { ...next[removed], list_id: destListId };
        }
        return next;
      });

      persistColumnOrder(nextCols);
    },
    [columnCardIds, persistColumnOrder, persistListOrder, readOnly, sortedLists],
  );

  const handleAddCard = async (listId) => {
    if (readOnly) return;
    const title = (newTitleByList[listId] || '').trim();
    if (!title) return;
    if (isStandalone && freeStandaloneUserId) {
      try {
        const raw = loadCricketFreeStandalone(freeStandaloneUserId);
        const listCards = raw.cards.filter((c) => c.list_id === listId);
        const sortOrder =
          listCards.length > 0 ? Math.max(...listCards.map((c) => Number(c.sort_order) || 0)) + 1 : 0;
        const now = new Date().toISOString();
        const newCard = {
          id: newStandaloneCricketId(),
          list_id: listId,
          widget_id: null,
          title,
          body: null,
          sort_order: sortOrder,
          cover_color: null,
          due_at: null,
          checklist_json: null,
          created_at: now,
          updated_at: now,
        };
        saveCricketFreeStandalone(freeStandaloneUserId, { lists: raw.lists, cards: [...raw.cards, newCard] });
        setNewTitleByList((m) => ({ ...m, [listId]: '' }));
        setComposerListId(null);
        await loadAll();
      } catch {
        notify(t('cricket:createError'), 'error');
      }
      return;
    }
    if (!authToken) return;
    try {
      const res = await fetch(`${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket/cards`, {
        method: 'POST',
        headers: authHeaders(authToken),
        body: JSON.stringify({ list_id: listId, title }),
      });
      if (!res.ok) {
        notify(t('cricket:createError'), 'error');
        return;
      }
      setNewTitleByList((m) => ({ ...m, [listId]: '' }));
      setComposerListId(null);
      await loadAll();
    } catch {
      notify(t('cricket:createError'), 'error');
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (readOnly) return;
    if (isStandalone && freeStandaloneUserId) {
      try {
        const raw = loadCricketFreeStandalone(freeStandaloneUserId);
        saveCricketFreeStandalone(freeStandaloneUserId, {
          lists: raw.lists,
          cards: raw.cards.filter((c) => c.id !== cardId),
        });
        setMoveMenuCardId(null);
        setEditingCardId(null);
        setMoreMenu((m) => (m && m.cardId === cardId ? null : m));
        setRichModalCardId((id) => (id === cardId ? null : id));
        await loadAll();
      } catch {
        notify(t('cricket:deleteError'), 'error');
      }
      return;
    }
    if (!authToken) return;
    try {
      const res = await fetch(`${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket/cards/${encodeURIComponent(cardId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) {
        notify(t('cricket:deleteError'), 'error');
        return;
      }
      setMoveMenuCardId(null);
      setEditingCardId(null);
      setMoreMenu((m) => (m && m.cardId === cardId ? null : m));
      setRichModalCardId((id) => (id === cardId ? null : id));
      await loadAll();
    } catch {
      notify(t('cricket:deleteError'), 'error');
    }
  };

  const commitCardTitle = async (cardId) => {
    if (readOnly) return;
    const title = editTitleDraft.trim();
    if (!title) return;
    if (isStandalone && freeStandaloneUserId) {
      try {
        const raw = loadCricketFreeStandalone(freeStandaloneUserId);
        const now = new Date().toISOString();
        const cards = raw.cards.map((c) =>
          c.id === cardId ? { ...c, title, updated_at: now } : c,
        );
        saveCricketFreeStandalone(freeStandaloneUserId, { lists: raw.lists, cards });
        setEditingCardId(null);
        await loadAll();
      } catch {
        notify(t('cricket:syncError'), 'error');
      }
      return;
    }
    if (!authToken) return;
    try {
      const res = await fetch(`${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket/cards/${encodeURIComponent(cardId)}`, {
        method: 'PATCH',
        headers: authHeaders(authToken),
        body: JSON.stringify({ title }),
      });
      if (!res.ok) {
        notify(t('cricket:syncError'), 'error');
        return;
      }
      setEditingCardId(null);
      await loadAll();
    } catch {
      notify(t('cricket:syncError'), 'error');
    }
  };

  const openMoveMenu = (cardId, listId) => {
    const others = sortedLists.filter((l) => l.id !== listId);
    setMoveTargetListId(others[0]?.id || '');
    setMoveMenuCardId(cardId);
    setEditingCardId(null);
  };

  const commitMoveCard = async (cardId) => {
    if (readOnly || !moveTargetListId) return;
    if (isStandalone && freeStandaloneUserId) {
      try {
        const raw = loadCricketFreeStandalone(freeStandaloneUserId);
        const destCards = raw.cards.filter((c) => c.list_id === moveTargetListId && c.id !== cardId);
        const nextSort = destCards.length > 0 ? Math.max(...destCards.map((c) => Number(c.sort_order) || 0)) + 1 : 0;
        const now = new Date().toISOString();
        const cards = raw.cards.map((c) =>
          c.id === cardId ? { ...c, list_id: moveTargetListId, sort_order: nextSort, updated_at: now } : c,
        );
        saveCricketFreeStandalone(freeStandaloneUserId, { lists: raw.lists, cards });
        setMoveMenuCardId(null);
        await loadAll();
      } catch {
        notify(t('cricket:syncError'), 'error');
      }
      return;
    }
    if (!authToken) return;
    try {
      const res = await fetch(`${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket/cards/${encodeURIComponent(cardId)}`, {
        method: 'PATCH',
        headers: authHeaders(authToken),
        body: JSON.stringify({ list_id: moveTargetListId }),
      });
      if (!res.ok) {
        notify(t('cricket:syncError'), 'error');
        return;
      }
      setMoveMenuCardId(null);
      await loadAll();
    } catch {
      notify(t('cricket:syncError'), 'error');
    }
  };

  const otherListsForMovePortal = useMemo(() => {
    if (!moveMenuCardId) return [];
    const c = cardsById[moveMenuCardId];
    if (!c?.list_id) return [];
    return sortedLists.filter((l) => l.id !== c.list_id);
  }, [sortedLists, moveMenuCardId, cardsById]);

  useLayoutEffect(() => {
    if (!moveMenuCardId || loading || errorKey) {
      setMovePanelPos(null);
      return;
    }
    const cardEl = document.querySelector(`[data-cricket-board-card="${moveMenuCardId}"]`);
    if (!cardEl) {
      setMovePanelPos(null);
      return;
    }
    const w = 236;
    const gap = 6;
    const minSpaceAbove = 200;
    const place = () => {
      const anchor =
        cardEl.querySelector('[data-cricket-board-move-anchor]') || cardEl;
      const r = anchor.getBoundingClientRect();
      const cardR = cardEl.getBoundingClientRect();
      const left = Math.max(8, Math.min(cardR.right - w, window.innerWidth - w - 8));
      if (r.top >= minSpaceAbove + gap) {
        setMovePanelPos({
          placement: 'above',
          left,
          bottom: window.innerHeight - r.top + gap - CRICKET_MOVE_PANEL_EXTRA_DOWN_PX,
        });
      } else {
        setMovePanelPos({
          placement: 'below',
          left,
          top: cardR.bottom + gap + CRICKET_MOVE_PANEL_EXTRA_DOWN_PX,
        });
      }
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [moveMenuCardId, columnCardIds, cardsById, sortedLists, loading, errorKey]);

  const topBar = (
    <header className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-white/20 bg-white/82 px-4 py-3 backdrop-blur-xl dark:border-slate-600/40 dark:bg-slate-900/75">
      <div className="flex min-w-0 items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <ArrowLeft size={18} />
          {t('cricket:backToPalco')}
        </button>
        <div className="flex min-w-0 items-center gap-2">
          <Lamp className="h-5 w-5 flex-shrink-0 text-amber-500" aria-hidden />
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold text-slate-900 dark:text-slate-100">{t('cricket:pageTitle')}</h1>
            <p className="truncate text-xs text-slate-500 dark:text-slate-400">{projectName || t('cricket:projectFallback')}</p>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {readOnly && (
          <span className="hidden max-w-[200px] truncate text-xs text-amber-700 dark:text-amber-300 sm:inline">{t('cricket:readOnlyHint')}</span>
        )}
        {syncing && <Loader2 className="h-4 w-4 animate-spin text-slate-400" aria-hidden />}
        <LanguageSwitcher compact />
        <button
          type="button"
          onClick={onToggleDarkMode}
          className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-medium text-slate-600 dark:border-slate-600 dark:text-slate-300"
        >
          {darkMode ? t('common:light', { defaultValue: 'Claro' }) : t('common:dark', { defaultValue: 'Escuro' })}
        </button>
      </div>
    </header>
  );

  if (loading) {
    return (
      <div className="relative flex h-[100dvh] flex-col overflow-hidden bg-slate-950">
        <CricketBrandedBackdrop />
        <div className="relative z-10 flex min-h-0 flex-1 flex-col">
          {topBar}
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-10 w-10 animate-spin text-blue-400" aria-hidden />
          </div>
        </div>
      </div>
    );
  }

  if (errorKey) {
    return (
      <div className="relative flex h-[100dvh] flex-col overflow-hidden bg-slate-950">
        <CricketBrandedBackdrop />
        <div className="relative z-10 flex min-h-0 flex-1 flex-col">
          {topBar}
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
            <p className="max-w-md text-sm text-slate-200 drop-shadow-sm">{t(`cricket:${errorKey}`)}</p>
            <button
              type="button"
              onClick={onBack}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              {t('cricket:backToPalco')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex h-[100dvh] flex-col overflow-hidden bg-slate-950">
      <CricketBrandedBackdrop />
      <div className="relative z-10 flex min-h-0 flex-1 flex-col">
        {topBar}
        <div className="min-h-0 flex-1 overflow-x-auto overflow-y-auto p-4">
          <DragDropContext
          onDragEnd={onDragEnd}
          onDragStart={() => {
            setComposerListId(null);
            setMoveMenuCardId(null);
            setMovePanelPos(null);
            setEditingCardId(null);
            setEditingListId(null);
            setNewListOpen(false);
            setMoreMenu(null);
            setRichModalCardId(null);
          }}
        >
          <div className="flex min-h-min min-w-max items-start gap-3 pb-8">
            <Droppable droppableId="cricket-columns" type="COLUMN" direction="horizontal">
              {(listDropProvided) => (
                <div
                  ref={listDropProvided.innerRef}
                  {...listDropProvided.droppableProps}
                  className="flex min-h-min items-start gap-3"
                >
                  {sortedLists.map((list, listIndex) => (
                    <Draggable
                      key={list.id}
                      draggableId={`list-${list.id}`}
                      index={listIndex}
                      isDragDisabled={readOnly}
                    >
                      {(colDragProvided, colSnapshot) => (
                        <div
                          ref={colDragProvided.innerRef}
                          {...colDragProvided.draggableProps}
                          className={`flex w-72 shrink-0 flex-col self-start rounded-xl bg-[#ebecf0] shadow-[inset_0_0_0_1px_rgba(9,30,66,0.08)] dark:bg-slate-800/95 dark:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)] ${
                            colSnapshot.isDragging ? 'ring-2 ring-amber-400/90 shadow-lg' : ''
                          }`}
                        >
                          <div className="flex flex-shrink-0 items-stretch gap-1 border-b border-slate-200/80 px-2 py-2 dark:border-slate-600/60">
                            <button
                              type="button"
                              className="mt-0.5 flex shrink-0 cursor-grab touch-manipulation items-center rounded p-1 text-slate-400 hover:bg-slate-200/80 dark:hover:bg-slate-700"
                              aria-label={t('cricket:dragColumn')}
                              disabled={readOnly}
                              {...(colDragProvided.dragHandleProps || {})}
                            >
                              <GripVertical size={16} aria-hidden />
                            </button>
                            <div className="min-w-0 flex-1">
                              {editingListId === list.id ? (
                                <input
                                  className="w-full rounded-md border border-blue-400 bg-white px-2 py-1 text-sm font-semibold text-slate-800 dark:bg-slate-950 dark:text-slate-100"
                                  value={editListTitleDraft}
                                  onChange={(e) => setEditListTitleDraft(e.target.value)}
                                  onBlur={() => void commitListTitle(list.id)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      void commitListTitle(list.id);
                                    }
                                    if (e.key === 'Escape') setEditingListId(null);
                                  }}
                                  autoFocus
                                  aria-label={t('cricket:renameColumn')}
                                />
                              ) : (
                                <h2
                                  className="truncate pr-1 text-sm font-semibold tracking-tight text-slate-700 dark:text-slate-200"
                                  title={list.title}
                                  onDoubleClick={() => {
                                    if (readOnly) return;
                                    setEditingListId(list.id);
                                    setEditListTitleDraft(list.title || '');
                                  }}
                                >
                                  {list.title}
                                </h2>
                              )}
                            </div>
                            {!readOnly && editingListId !== list.id && (
                              <button
                                type="button"
                                className="mt-0.5 shrink-0 rounded p-1 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700"
                                title={t('cricket:renameColumn')}
                                aria-label={t('cricket:renameColumn')}
                                onClick={() => {
                                  setEditingListId(list.id);
                                  setEditListTitleDraft(list.title || '');
                                }}
                              >
                                <Pencil size={14} aria-hidden />
                              </button>
                            )}
                          </div>
                          <Droppable droppableId={list.id} isDropDisabled={readOnly} type="DEFAULT">
                  {(dropProvided, snapshot) => (
                    <div
                      ref={dropProvided.innerRef}
                      {...dropProvided.droppableProps}
                      className={`flex flex-col gap-2 px-2 pb-2 ${
                        snapshot.isDraggingOver ? 'rounded-lg bg-slate-300/35 dark:bg-slate-600/40' : ''
                      }`}
                    >
                      {(columnCardIds[list.id] || []).length === 0 && composerListId !== list.id && (
                        <p className="px-1 py-1 text-center text-xs text-slate-500 dark:text-slate-400">{t('cricket:emptyColumn')}</p>
                      )}
                      {(columnCardIds[list.id] || []).map((cardId, index) => {
                        const card = cardsById[cardId];
                        if (!card) return null;
                        const checklistItems = parseCricketChecklist(card.checklist_json);
                        const checklistDone = checklistItems.filter((x) => x.done).length;
                        const coverHex = cardCoverHex(card.cover_color);
                        let dueLabel = null;
                        if (card.due_at) {
                          const d = new Date(String(card.due_at).replace(' ', 'T'));
                          if (!Number.isNaN(d.getTime())) {
                            dueLabel = d.toLocaleDateString(i18n.resolvedLanguage || i18n.language, {
                              day: 'numeric',
                              month: 'short',
                            });
                          }
                        }
                        return (
                          <Draggable key={card.id} draggableId={card.id} index={index} isDragDisabled={readOnly}>
                            {(dragProvided, dragSnapshot) => (
                              <div
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                                data-cricket-board-card={card.id}
                                className={`relative rounded-lg border-0 bg-white text-sm text-slate-800 shadow-[0_1px_0_#091e4240] dark:bg-slate-900 dark:text-slate-100 dark:shadow-[0_1px_0_rgba(0,0,0,0.4)] ${
                                  editingCardId === card.id
                                    ? 'ring-2 ring-blue-500 ring-offset-1 ring-offset-[#ebecf0] dark:ring-offset-slate-800'
                                    : 'group'
                                } ${dragSnapshot.isDragging ? 'z-[1] ring-2 ring-blue-400 ring-offset-1' : ''}`}
                              >
                                {!!coverHex && (
                                  <div
                                    className="min-h-[52px] w-full shrink-0 rounded-t-lg"
                                    style={{ backgroundColor: coverHex }}
                                    aria-hidden
                                  />
                                )}
                                {editingCardId !== card.id && (
                                  <div className="pointer-events-none absolute right-1.5 top-1.5 z-10 flex gap-0.5 rounded-md border border-slate-200/80 bg-white/95 p-0.5 shadow-sm opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 dark:border-slate-600 dark:bg-slate-800/95">
                                    <button
                                      type="button"
                                      className="pointer-events-auto rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-700 dark:hover:text-slate-100"
                                      title={t('cricket:moreCardActions')}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const r = e.currentTarget.getBoundingClientRect();
                                        setMoveMenuCardId(null);
                                        setMoreMenu({ cardId: card.id, listId: list.id, top: r.bottom + 4, left: r.right - 224 });
                                      }}
                                    >
                                      <MoreHorizontal size={14} aria-hidden />
                                    </button>
                                    <button
                                      type="button"
                                      disabled={readOnly}
                                      className="pointer-events-auto rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-40 dark:hover:bg-slate-700 dark:hover:text-slate-100"
                                      title={t('cricket:editCard')}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setMoveMenuCardId(null);
                                        setEditingCardId(card.id);
                                        setEditTitleDraft(card.title || '');
                                      }}
                                    >
                                      <Pencil size={14} aria-hidden />
                                    </button>
                                    {!!card.widget_id && typeof onFocusLinkedWidget === 'function' && (
                                      <button
                                        type="button"
                                        className="pointer-events-auto rounded p-1 text-slate-500 hover:bg-amber-50 hover:text-amber-700 dark:hover:bg-amber-950/50 dark:hover:text-amber-300"
                                        title={t('cricket:goToStageWidget')}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onFocusLinkedWidget(card.widget_id);
                                        }}
                                      >
                                        <Crosshair size={14} aria-hidden />
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      disabled={readOnly}
                                      className="pointer-events-auto rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-40 dark:hover:bg-red-950/40 dark:hover:text-red-400"
                                      title={t('cricket:deleteCard')}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteCard(card.id);
                                      }}
                                    >
                                      <Trash2 size={14} aria-hidden />
                                    </button>
                                  </div>
                                )}
                                {editingCardId === card.id ? (
                                  <div className="space-y-2 p-3">
                                    <textarea
                                      className="w-full min-h-[4.5rem] resize-y rounded-md border border-slate-300 bg-white px-2 py-2 text-sm text-slate-800 shadow-inner focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100"
                                      value={editTitleDraft}
                                      onChange={(e) => setEditTitleDraft(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                          e.preventDefault();
                                          commitCardTitle(card.id);
                                        }
                                      }}
                                      rows={3}
                                      aria-label={t('cricket:editCard')}
                                      autoFocus
                                    />
                                    <div className="flex flex-wrap gap-2">
                                      <button
                                        type="button"
                                        onClick={() => commitCardTitle(card.id)}
                                        className="rounded bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700"
                                      >
                                        {t('cricket:saveCardTitle')}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setEditingCardId(null)}
                                        className="rounded px-2 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                                      >
                                        {t('cricket:cancelComposer')}
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div
                                    className="flex cursor-grab items-start gap-2 px-3 py-2 pr-14 active:cursor-grabbing"
                                    data-cricket-board-move-anchor
                                  >
                                    <button
                                      type="button"
                                      className="mt-0.5 shrink-0 cursor-pointer rounded p-0.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                                      title={t('cricket:openCard')}
                                      aria-label={t('cricket:openCard')}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setMoveMenuCardId(null);
                                        setMoreMenu(null);
                                        setRichModalCardId(card.id);
                                      }}
                                    >
                                      <SquareArrowOutUpRight size={16} aria-hidden />
                                    </button>
                                    <div className="min-w-0 flex-1">
                                      <div className="break-words font-medium">{card.title}</div>
                                      {(dueLabel || checklistItems.length > 0) && (
                                        <div className="mt-1 flex flex-wrap gap-2 text-[10px] font-medium text-slate-600 dark:text-slate-300">
                                          {dueLabel && (
                                            <span className="rounded bg-slate-100 px-1.5 py-0.5 dark:bg-slate-800">{dueLabel}</span>
                                          )}
                                          {checklistItems.length > 0 && (
                                            <span className="rounded bg-slate-100 px-1.5 py-0.5 dark:bg-slate-800">
                                              ✓ {checklistDone}/{checklistItems.length}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                      {!!card.widget_id && (
                                        <span className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                                          <span className="inline-flex items-center gap-0.5" title={t('cricket:linkedToElement')}>
                                            <Link2 size={12} aria-hidden />
                                            {t('cricket:linkedToElement')}
                                          </span>
                                          <Lamp
                                            size={12}
                                            className="flex-shrink-0"
                                            style={{ color: CRICKET_LIST_LAMP_HEX[list.list_key] || '#a855f7' }}
                                            aria-hidden
                                          />
                                        </span>
                                      )}
                                      {(() => {
                                        const raw = card.body && String(card.body).trim();
                                        if (!raw) return null;
                                        const prev = htmlToPlainPreview(raw);
                                        if (!prev) return null;
                                        return (
                                          <p
                                            className="mt-1 line-clamp-2 whitespace-pre-wrap break-words text-[11px] font-normal leading-snug text-slate-500 dark:text-slate-400"
                                            title={prev}
                                          >
                                            {prev}
                                          </p>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {dropProvided.placeholder}
                      {!readOnly &&
                        (composerListId === list.id ? (
                          <div className="rounded-lg border border-slate-300 bg-white p-2 shadow-[0_1px_0_#091e4240] dark:border-slate-600 dark:bg-slate-900">
                            <input
                              ref={list.id === composerListId ? composerInputRef : undefined}
                              className="w-full rounded-md border border-slate-200 bg-white px-2 py-2 text-sm text-slate-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100"
                              placeholder={t('cricket:cardTitlePlaceholder')}
                              value={newTitleByList[list.id] || ''}
                              onChange={(e) => setNewTitleByList((m) => ({ ...m, [list.id]: e.target.value }))}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddCard(list.id);
                                }
                                if (e.key === 'Escape') {
                                  setComposerListId(null);
                                  setNewTitleByList((m) => ({ ...m, [list.id]: '' }));
                                }
                              }}
                            />
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleAddCard(list.id)}
                                className="rounded bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700"
                              >
                                {t('cricket:addCardSubmit')}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setComposerListId(null);
                                  setNewTitleByList((m) => ({ ...m, [list.id]: '' }));
                                }}
                                className="rounded px-2 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                              >
                                {t('cricket:cancelComposer')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setComposerListId(list.id);
                              setNewTitleByList((m) => ({ ...m, [list.id]: '' }));
                            }}
                            className="flex w-full items-center gap-2 rounded-lg py-2 pl-2 pr-2 text-left text-sm font-medium text-slate-600 transition-colors hover:bg-[#00000014] dark:text-slate-300 dark:hover:bg-white/10"
                          >
                            <Plus size={18} strokeWidth={2.25} className="shrink-0 opacity-70" aria-hidden />
                            {t('cricket:addCardLink')}
                          </button>
                        ))}
                    </div>
                  )}
                </Droppable>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {listDropProvided.placeholder}
                </div>
              )}
            </Droppable>
            {!readOnly && (
              <div className="flex w-72 shrink-0 flex-col self-start rounded-xl border-2 border-dashed border-slate-300/80 bg-[#ebecf0]/60 p-3 dark:border-slate-600/70 dark:bg-slate-800/40">
                {newListOpen ? (
                  <div className="space-y-2">
                    <input
                      className="w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm text-slate-800 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100"
                      placeholder={t('cricket:listTitlePlaceholder')}
                      value={newListTitle}
                      onChange={(e) => setNewListTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void handleCreateList();
                        }
                        if (e.key === 'Escape') {
                          setNewListOpen(false);
                          setNewListTitle('');
                        }
                      }}
                      autoFocus
                    />
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => void handleCreateList()}
                        className="rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                      >
                        {t('cricket:addColumnSubmit')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setNewListOpen(false);
                          setNewListTitle('');
                        }}
                        className="rounded px-2 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200 dark:text-slate-300 dark:hover:bg-slate-700"
                      >
                        {t('cricket:cancelComposer')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setNewListOpen(true)}
                    className="flex w-full items-center justify-center gap-2 rounded-lg py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-200/70 dark:text-slate-300 dark:hover:bg-slate-700/50"
                  >
                    <Plus size={18} strokeWidth={2.25} aria-hidden />
                    {t('cricket:addColumn')}
                  </button>
                )}
              </div>
            )}
          </div>
          </DragDropContext>
        </div>
      </div>
      <CricketRichCardModal
        open={!!richModalCardId && !!cardsById[richModalCardId]}
        card={richModalCardId ? cardsById[richModalCardId] : null}
        sortedLists={sortedLists}
        layoutId={layoutId}
        authToken={authToken}
        readOnly={readOnly}
        onClose={() => setRichModalCardId(null)}
        onSaved={loadAll}
        onFocusLinkedWidget={onFocusLinkedWidget}
        showToast={showToast}
        persistCardPatch={isStandalone ? persistStandaloneCardPatch : undefined}
      />
      {moreMenu && cardsById[moreMenu.cardId] ? (
        <CricketCardActionsMenu
          open
          anchor={{ top: moreMenu.top, left: moreMenu.left }}
          onClose={() => setMoreMenu(null)}
          readOnly={readOnly}
          layoutId={layoutId}
          authToken={authToken}
          card={cardsById[moreMenu.cardId]}
          listId={moreMenu.listId}
          sortedLists={sortedLists}
          onOpenMove={() => {
            const mid = moreMenu.cardId;
            const lid = moreMenu.listId;
            setMoreMenu(null);
            openMoveMenu(mid, lid);
          }}
          onAfterMutation={loadAll}
          showToast={showToast}
          persistCardPatch={isStandalone ? persistStandaloneCardPatch : undefined}
          duplicateCardLocal={isStandalone ? duplicateStandaloneCard : undefined}
        />
      ) : null}

      {moveMenuCardId &&
        movePanelPos &&
        otherListsForMovePortal.length > 0 &&
        createPortal(
          <div
            data-cricket-board-move-portal
            role="dialog"
            aria-label={t('cricket:moveCardToList')}
            className="fixed z-[21000] min-w-[220px] rounded-lg border border-slate-200 bg-white p-3 shadow-lg dark:border-slate-600 dark:bg-slate-900"
            style={
              movePanelPos.placement === 'above'
                ? { left: movePanelPos.left, bottom: movePanelPos.bottom, top: 'auto' }
                : { left: movePanelPos.left, top: movePanelPos.top, bottom: 'auto' }
            }
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="mb-2 text-xs font-semibold text-slate-700 dark:text-slate-200">{t('cricket:moveCardToList')}</div>
            <select
              className="mb-3 w-full rounded-md border border-slate-200 bg-white px-2 py-2 text-sm text-slate-800 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100"
              value={moveTargetListId}
              onChange={(e) => setMoveTargetListId(e.target.value)}
            >
              {otherListsForMovePortal.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.title}
                </option>
              ))}
            </select>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => commitMoveCard(moveMenuCardId)}
                className="rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
              >
                {t('cricket:moveCardSubmit')}
              </button>
              <button
                type="button"
                onClick={() => setMoveMenuCardId(null)}
                className="rounded px-2 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                {t('cricket:cancelComposer')}
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
